import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import React, { FunctionComponent, useContext } from 'react';
import clsx from 'clsx';
import { AccessTime, InfoOutlined } from '@material-ui/icons';
import {
  BillInfo,
  AutoPayInfo,
  ViewBillDetails,
} from '../../../__generated__/pge-types';
import { useTranslation } from '../../../hooks/useTranslation';
import colors from '../../../themes/main-colors';
import { toSentenceCase } from '../../../util/format';
import { Button, CardContent, Paper, Typography } from '@material-ui/core';
import ErrorOverlay from '../../error/error-overlay';
import ROUTES from '../../../routes';
import { navigate } from 'gatsby';
import BillIcon from '../../../static/bill-icon.svg';
import BillingOptions from '../billing-options/BillingOptions';
import CurrentBillPaymentSummary from '../current-bill-payment-summary';
import { Option } from '../program-options-list/ProgramOptionsList';
import { useIsMobile } from '../../../util/style-utils';
import {
  LanguageContext,
  SupportedLanguages,
} from '../../../providers/LanguageProvider';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    title: {
      display: 'block',
      backgroundColor: colors.lightBlue1,
      marginTop: '3px',
      padding: '13px 24px',
      borderRadius: '3px 3px 0px 0px',
    },
    titleText: {
      color: colors.noirBlur,
      fontSize: '20px',
      fontWeight: 'normal',
    },
    container: {
      padding: theme.spacing(2),
    },
    cardLayout: {
      width: '100%',
      borderRadius: '4px',
      boxShadow: `inset 0px 2px 0px ${colors.sparkBlue}, 0px 3px 6px #00000029`,
    },
    autopayPaperCard: {
      boxShadow: 'inset 0px 2px 0px #27788C, 0px 3px 6px #00000029',
    },
    noBillPaperCard: {
      boxShadow: 'inset 0px 2px 0px #1C304A, 0px 3px 6px #00000029',
    },
    pastDuePaperCard: {
      boxShadow: 'inset 0px 2px 0px red, 0px 3px 6px #00000029',
    },
    divider: {
      height: '1px',
    },
  }),
);

interface CurrentBillProps {
  totalPendingCharges?: number | null;
  billInfo: BillInfo | null;
  autoPay: AutoPayInfo | null;
  isPendingDisconnect: boolean | undefined;
  billDetails: ViewBillDetails | null;
  isSummaryBillAccount: boolean | undefined;
  optionList: Option[];
  isTpaEligible: boolean;
  isNewAccount: boolean | null | undefined;
  isResAccount: boolean;
}

const CurrentBill: FunctionComponent<CurrentBillProps> = ({
  totalPendingCharges,
  billInfo,
  autoPay,
  isPendingDisconnect,
  billDetails,
  isSummaryBillAccount,
  optionList,
  isTpaEligible,
  isNewAccount,
  isResAccount,
}) => {
  const classes = useStyles();
  const { language } = useContext(LanguageContext);
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const papercardStyle = clsx({
    [classes.cardLayout]: true, //always applies
    [classes.noBillPaperCard]: !Boolean(billDetails),
    [classes.pastDuePaperCard]: isPendingDisconnect,
    [classes.autopayPaperCard]: Boolean(autoPay?.isEnrolled),
  });

  const understandingMyBillRoute = {
    [SupportedLanguages.English]: isResAccount
      ? ROUTES.UNDERSTANDING_MY_BILL
      : ROUTES.UNDERSTANDING_MY_BUSINESS_BILL,
    [SupportedLanguages.Spanish]: isResAccount
      ? ROUTES.UNDERSTANDING_MY_BILL_SPANISH
      : ROUTES.UNDERSTANDING_MY_BUSINESS_BILL_SPANISH,
  };

  const route =
    understandingMyBillRoute[language] ||
    understandingMyBillRoute[SupportedLanguages.English];

  return (
    <Paper className={papercardStyle}>
      <CardContent style={{ padding: '0' }}>
        {billInfo && autoPay ? (
          <Grid container direction="row">
            <Grid item container direction="row" className={classes.title}>
              <Grid item xs={12}>
                <Typography className={classes.titleText} variant="h4">
                  {toSentenceCase(t('CURRENT_BILL'))}
                </Typography>
              </Grid>
            </Grid>

            <Grid item container direction="row">
              <CurrentBillPaymentSummary
                totalPendingCharges={totalPendingCharges}
                billInfo={billInfo}
                autoPay={autoPay}
                amountCreditDisplay={'CR'}
                isMobile={isMobile}
                viewBillDetails={billDetails}
                isSummaryBillAccount={isSummaryBillAccount}
                isPendingDisconnect={isPendingDisconnect}
                isNewAccount={isNewAccount}
              />
            </Grid>

            {!isNewAccount && (
              <Grid
                item
                container
                direction="row"
                spacing={2}
                className={classes.container}
                justify="center"
              >
                <Grid item xs={isMobile ? 12 : 4}>
                  <Button
                    fullWidth
                    onClick={() => navigate(ROUTES.PAYMENT_HISTORY)}
                    size={'large'}
                    variant="outlined"
                    style={{
                      whiteSpace: 'nowrap',
                      background: colors.white,
                      color: colors.sparkBlue,
                    }}
                    endIcon={<BillIcon width="18px" height="18px" />}
                  >
                    {t('VIEW_BILL_HISTORY')}
                  </Button>
                </Grid>
                <Grid item xs={isMobile ? 12 : 4}>
                  <Button
                    fullWidth
                    onClick={() => navigate(route)}
                    size={'large'}
                    variant="outlined"
                    style={{
                      whiteSpace: 'nowrap',
                      background: colors.white,
                      color: colors.sparkBlue,
                    }}
                    endIcon={<InfoOutlined width="22px" height="22px" />}
                  >
                    {t('UNDERSTANDING_MY_BILL')}
                  </Button>
                </Grid>

                {isTpaEligible && (
                  <Grid item xs={isMobile ? 12 : 4}>
                    <Button
                      fullWidth
                      onClick={() => navigate(ROUTES.NEED_MORE_TIME_TO_PAY)}
                      size={'large'}
                      variant="outlined"
                      style={{
                        whiteSpace: 'nowrap',
                        background: colors.white,
                        color: colors.sparkBlue,
                      }}
                      endIcon={<AccessTime />}
                    >
                      {t('NMTTP')}
                    </Button>
                  </Grid>
                )}
              </Grid>
            )}
            <Grid
              item
              xs={12}
              style={{ padding: '0px 16px', marginTop: '8px' }}
            >
              <Divider className={classes.divider} />
            </Grid>
            <Grid item xs={12}>
              <BillingOptions
                isMobile={isMobile}
                optionList={optionList}
                defaultExpand={!Boolean(billInfo?.billDetails?.amountDue)}
              />
            </Grid>
          </Grid>
        ) : (
          <ErrorOverlay title={t('CURRENT_BILL')} />
        )}
      </CardContent>
    </Paper>
  );
};

export default CurrentBill;
